import { Link } from '@tanstack/react-router'

import { BookOpenText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'

import { useOnboardingActions } from '../store'

import type { OnboardingTask as TaskType } from '../types'
import type { LinkOptions } from '@tanstack/react-router'

type OnboardingTaskProps = {
	index: number
	task: TaskType
	link: LinkOptions['to']
}

export const OnboardingTask = ({ index, task, link }: OnboardingTaskProps) => {
	const { markTaskAsSkipped } = useOnboardingActions()

	const isSkipped = task.skipped
	const isCompleted = task.completed

	const isTaskDone = isSkipped || isCompleted

	return (
		<div className="flex flex-row items-center gap-4">
			<div className="bg-secondary text-secondary-foreground hidden aspect-square size-8 flex-none items-center justify-center rounded-full text-sm sm:flex">
				{index}
			</div>

			<div className="flex flex-col">
				<p className="flex flex-row items-center gap-2">
					{task.name}
					{task.docs && (
						<Tooltip delay={100}>
							<TooltipTrigger
								render={
									<a href={task.docs} target="_blank" rel="noopener noreferrer">
										<BookOpenText className="size-4" />
									</a>
								}
							/>
							<TooltipContent>Help Docs</TooltipContent>
						</Tooltip>
					)}
				</p>

				<p className="text-muted-foreground pr-4 text-sm">{task.description}</p>
			</div>

			<div className="flex-auto"></div>

			<div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
				{isTaskDone ? (
					<Button variant="outline" disabled>
						{isSkipped ? 'Skipped' : 'Completed'}
					</Button>
				) : (
					<>
						<Button
							nativeButton={false}
							render={
								<Link
									className="bg-primary text-foreground hover:bg-primary/80 dark:text-white"
									to={link}
								>
									Start
								</Link>
							}
						/>

						{!task.required && (
							<Button
								variant="ghost"
								onClick={() => markTaskAsSkipped(task.id)}
							>
								Skip
							</Button>
						)}
					</>
				)}
			</div>
		</div>
	)
}

export const TaskSkeleton = () => {
	return (
		<div className="flex h-11 flex-row items-center gap-4">
			<div className="bg-secondary text-secondary-foreground hidden aspect-square size-8 flex-none items-center justify-center rounded-full text-sm sm:flex">
				<Skeleton className="size-4 rounded-full" />
			</div>

			<div className="flex w-full flex-col items-start justify-start gap-1">
				<Skeleton className="h-4 w-96" />
				<Skeleton className="h-4 w-full" />
			</div>

			<div className="flex-auto"></div>

			<div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
				<Skeleton className="h-8 w-24" />

				<Skeleton className="h-8 w-24" />
			</div>
		</div>
	)
}
